import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const API = "/api/v1/distributor-links";

/**
 * Super admin: links two distributors by giving them the same parent id (one of them is the parent). With "share brands with
 * salesmen" on, every salesman of the group can create manual invoices in the app for any linked distributor, choosing the
 * distributor and its brand.
 */
const DistributorLinks = () => {
  const [distributors, setDistributors] = useState([]);
  const [groups, setGroups] = useState([]);
  const [parentId, setParentId] = useState("");
  const [childId, setChildId] = useState("");
  const [share, setShare] = useState(true);
  const [multiCompany, setMultiCompany] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [list, linked] = await Promise.all([api.get(`${API}/distributors`), api.get(API)]);
      setDistributors(Array.isArray(list) ? list : []);
      setGroups(Array.isArray(linked) ? linked : []);
    } catch (error) {
      toast.error(error.message || "Failed to load distributors (super admin only)");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const link = async (e) => {
    e.preventDefault();
    if (!parentId || !childId) return toast.error("Pick both distributors");
    if (parentId === childId) return toast.error("Pick two different distributors");
    setSaving(true);
    try {
      await api.post(API, {
        parentDistributorId: Number(parentId),
        childDistributorId: Number(childId),
        shareBrandsWithSalesmen: share,
        multiCompanyEnabled: multiCompany,
      });
      toast.success("Distributors linked");
      setChildId("");
      await load();
    } catch (error) {
      toast.error(error.message || "Could not link the distributors");
    } finally {
      setSaving(false);
    }
  };

  const toggleShare = async (group) => {
    try {
      const child = group.members.find((m) => m.distributorId !== group.parentDistributorId) || group.members[0];
      await api.post(API, {
        parentDistributorId: group.parentDistributorId,
        childDistributorId: child.distributorId,
        shareBrandsWithSalesmen: !group.shareBrandsWithSalesmen,
      });
      await load();
    } catch (error) {
      toast.error(error.message || "Could not update the group");
    }
  };

  const unlink = async (member) => {
    if (!window.confirm(`Take ${member.name} out of its group?`)) return;
    try {
      await api.del(`${API}/${member.distributorId}`);
      toast.success("Unlinked");
      await load();
    } catch (error) {
      toast.error(error.message || "Could not unlink");
    }
  };

  const label = (d) => `${d.name || "Distributor"} (#${d.distributorId})`;

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <div className="page-subtitle" style={{ marginBottom: 12 }}>
        Link two distributors under one parent id (the parent is one of them). With <strong>Share brands with salesmen</strong>, the salesmen of
        both distributors can pick either distributor — and its brand — when they create a manual invoice in the app; the invoice is created in
        the distributor picked, with its products, stock and prices.
      </div>
      <form onSubmit={link} className="page-panel" style={{ padding: "14px 16px" }}>
        <div className="form-grid">
          <div className="form-group">
            <label>Parent distributor (its id becomes the parent id)</label>
            <select value={parentId} onChange={(e) => setParentId(e.target.value)} required>
              <option value="">Select</option>
              {distributors.map((d) => (
                <option key={d.distributorId} value={d.distributorId}>
                  {label(d)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Distributor to link</label>
            <select value={childId} onChange={(e) => setChildId(e.target.value)} required>
              <option value="">Select</option>
              {distributors
                .filter((d) => String(d.distributorId) !== String(parentId))
                .map((d) => (
                  <option key={d.distributorId} value={d.distributorId}>
                    {label(d)}
                    {d.parentDistributorId ? ` — in group #${d.parentDistributorId}` : ""}
                  </option>
                ))}
            </select>
          </div>
          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} />
              Share brands with salesmen (salesmen of both can create app invoices for either distributor)
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={multiCompany} onChange={(e) => setMultiCompany(e.target.checked)} />
              Multi-company dropdown for the super admin
            </label>
          </div>
        </div>
        <div className="modal-actions">
          <button type="submit" className="primary" disabled={saving}>
            {saving ? "Linking..." : "Link distributors"}
          </button>
        </div>
      </form>

      <h3 style={{ fontWeight: 600, margin: "18px 0 6px" }}>Linked groups</h3>
      {groups.length === 0 ? (
        <div className="page-subtitle">No distributors are linked yet.</div>
      ) : (
        groups.map((group) => (
          <div key={group.parentDistributorId} className="page-panel" style={{ padding: "10px 14px", marginBottom: 10 }}>
            <div className="flex items-center justify-between" style={{ gap: 8, flexWrap: "wrap" }}>
              <strong>
                Parent id #{group.parentDistributorId} — {group.parentName}
              </strong>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={group.shareBrandsWithSalesmen} onChange={() => toggleShare(group)} />
                Share brands with salesmen
              </label>
            </div>
            <div className="table-wrapper" style={{ marginTop: 6 }}>
              <table>
                <thead>
                  <tr>
                    <th>Distributor ID</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Multi-company</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {group.members.map((member) => (
                    <tr key={member.distributorId}>
                      <td>{member.distributorId}</td>
                      <td>{member.name}</td>
                      <td>{member.distributorId === group.parentDistributorId ? <span className="badge badge-blue">Parent</span> : "Linked"}</td>
                      <td>{member.multiCompanyEnabled ? "Yes" : "No"}</td>
                      <td>
                        <button className="secondary" onClick={() => unlink(member)}>
                          Unlink
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default DistributorLinks;
